import { useApp } from '@/App';
import { useModals } from '@/components/modals/ModalProvider';
import {
	calculationTypeOptions,
	SIMULATION_ASSET_CONDITION,
	SIMULATION_ASSET_TYPE,
	SIMULATION_CALCULATION_TYPE_OPTIONS,
} from '@/constants/simulation.const';
import DataRow from '@/libs/antd/components/DataRow';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import rule from '@/libs/antd/rules';
import {
	useSelectAsset,
	useSelectAssetBrand,
	useSelectKaroseriCompany,
	useSelectMasterSupplier,
	useSelectProduct,
} from '@/libs/fleet-api/hooks/master-data';
import {
	useCalculateFiduciaryFee,
	useCalculateServicingFee,
	useGetCalculateSimulation,
	useSimulationDetail,
} from '@/libs/fleet-api/hooks/simulation';
import {
	calculatePHJMBAmount,
	calculateSimulation,
	CalculateSimulationRequest,
	CalculateSimulationResponse,
	editSimulationProduct,
	SaveSimulationRequest,
} from '@/libs/fleet-api/services/simulation';
import { uploader } from '@/libs/fleet-api/uploader';
import { enumToLabel } from '@/utils/enum-to-label';
import { mapOptionsToSelect } from '@/utils/map-options-to-select';
import { ArrowLeftOutlined, InfoCircleFilled } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import {
	Alert,
	Button,
	Checkbox,
	Col,
	Collapse,
	DatePicker,
	Divider,
	Form,
	Input,
	InputNumber,
	message,
	Radio,
	Row,
	Select,
	Spin,
} from 'antd';
import { useWatch } from 'antd/es/form/Form';
import { FC, useEffect, useMemo, useState } from 'react';
import SimInsurance from './Insurance/SimInsurance';
import SimulationPagePreview from './SimulationPagePreview';
import { mapSimulationDetailToFormValues } from './utils/map-simulation-detail-to-form-values';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { validatePaymentBreakdown } from './utils/validate-payment-breakdown';
import { NAPNJFModal, useNAPNJFModal } from '@/pages/nap/NAPDownloadPage';
import { NAPJFModal, useNAPJFModal } from '@/pages/nap/NAPJFDownloadPage';
import dayjs from 'dayjs';
import { useUserData } from '@/libs/fleet-api/hooks';

type SimulationFormData = CalculateSimulationRequest;

type FRMSimulationPageProps = {
	spfId?: number;
	facilityId?: number;
	simulationId?: number;
	viewOnly?: boolean;
	onBack?: () => void;
	showResult?: boolean;
};

const FRMSimulationPage: FC<FRMSimulationPageProps> = ({
	spfId,
	facilityId,
	simulationId,
	viewOnly,
	onBack,
	showResult,
}) => {
	const { navigate } = useApp();
	const { openCustomerDetail, openCustomerGroupDetail, openPreviewModal } =
		useModals();
	const userData = useUserData();

	const napNJFModal = useNAPNJFModal();
	const napJFModal = useNAPJFModal();
	const { form, fieldName } = useForm<SimulationFormData>();

	const {
		data: simulationDetailData,
		isFetched,
		error,
	} = useSimulationDetail(
		simulationId!
			? { simulationId }
			: {
					spfId: spfId!,
					facilityId: facilityId!,
					calculationTypeId: 0,
				},
	);

	const calculationTypeIdInForm = useWatch(
		fieldName('calculationTypeId'),
		form,
	);
	const facilityIdInForm = useWatch(fieldName('facilityId'), form);
	const assetType =
		useWatch(fieldName('assetType'), form) || simulationDetailData?.assetType;
	const assetCondition = useWatch(fieldName('assetConditionId'), form);
	const isSplitPo = useWatch(fieldName('isSplitPo'), form);
	const brandId = useWatch(fieldName('assetBrandId'), form);
	const assetTypeId = simulationDetailData?.assetTypeId;
	const karoseriCalculationUnitPrice = useWatch(
		fieldName(['karoseriCalculation', 'unitPrice']),
		form,
	);
	const chasisCalculationUnitPrice = useWatch(
		fieldName(['chasisCalculation', 'unitPrice']),
		form,
	);
	const totalUnit = useWatch(fieldName('totalUnit'), form);

	const karoseriCalculationDP = useWatch(
		fieldName(['karoseriCalculation', 'downPaymentPercentage']),
		form,
	);
	const chasisCalculationDP = useWatch(
		fieldName(['chasisCalculation', 'downPaymentPercentage']),
		form,
	);

	const isHeavyEquipmentAndOtherComponent = useWatch(
		fieldName('isHeavyEquipmentAndOtherComponent'),
		form,
	);
	const otrHeHe = useWatch(fieldName('otrHeavyEquipmentAmount'), form);
	const otrHeOth = useWatch(
		fieldName('otrOtherHeavyEquipmentComponentAmount'),
		form,
	);

	const isChasisAndKaroseri = useWatch(fieldName('isChasisAndKaroseri'), form);
	const otrChasKarChas = useWatch(fieldName('otrChasisAmount'), form);
	const otrChasKarKar = useWatch(fieldName('otrKaroseriAmount'), form);
	const isBundlingFiduciaryFee = useWatch(
		fieldName('isBundlingFiduciaryFee'),
		form,
	);
	const requestedOtrAmount = useWatch(fieldName('requestedOtrAmount'), form);
	const fiduciaryFee = useWatch(fieldName('fiduciaryFee'), form);
	const tenorInYear = useWatch(fieldName('tenorInYear'), form);

	const provisionPercentage = useWatch(fieldName('provisionPercentage'), form);
	const otrSubsidyAmount = useWatch(fieldName('otrSubsidyAmount'), form);
	const downPaymentPercentage = useWatch(
		fieldName('downPaymentPercentage'),
		form,
	);
	const assetManufactureYear = useWatch(
		fieldName('assetManufactureYear'),
		form,
	);

	const assetConditionInForm = useWatch(fieldName('assetConditionId'), form);
	const assetTypeInForm = useWatch(fieldName('assetType'), form);

	// Calculation type modal state
	const [calcModalOpen, setCalcModalOpen] = useState(false);

	// Confirmation modal state
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [pendingFormData, setPendingFormData] =
		useState<SimulationFormData | null>(null);

	const [simulationResult, setSimulationResult] =
		useState<CalculateSimulationResponse>();

	const [simulationFormValues, setSimulationFormValues] =
		useState<SaveSimulationRequest>();

	const [calcType, setCalcType] =
		useState<SIMULATION_CALCULATION_TYPE_OPTIONS>(1);

	// New state to control showing calculation result
	const [showCalculation, setShowCalculation] = useState(false);

	// New state to control if we should show the result view
	const [shouldShowResult, setShouldShowResult] = useState(showResult || false);

	const calcTypeInForm: SIMULATION_CALCULATION_TYPE_OPTIONS = useWatch(
		fieldName('calculationTypeId'),
		form,
	);

	useEffect(() => {
		const currentValue = form.getFieldValue(
			fieldName('isBundlingFiduciaryFee'),
		);
		if (
			(currentValue === undefined || currentValue === null) &&
			form &&
			fieldName
		) {
			form.setFieldValue(fieldName('isBundlingFiduciaryFee'), false);
		}
	}, [form, fieldName]);

	useEffect(() => {
		if (!viewOnly) {
			if (isFetched && !simulationDetailData?.calculationType) {
				setCalcModalOpen(true);
			}
		}
	}, [isFetched, simulationDetailData?.calculationType, viewOnly]);

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	const {
		options: dealerSupplierOptions,
		loading: dealerSupplierLoading,
		onSearch: onDealerSupplierSearch,
		onPopupScroll: onDealerSupplierPopupScroll,
	} = useSelectMasterSupplier();

	// const {
	// 	options: karoseriCompanyOptions,
	// 	loading: karoseriCompanyLoading,
	// 	onSearch: onKaroseriCompanySearch,
	// 	onPopupScroll: onKaroseriCompanyPopupScroll,
	// } = useSelectKaroseriCompany();

	// const { options: karoseriCompanyOptions, loading: karoseriCompanyLoading } =
	// 	useSelectKaroseriCompany(dealerSupplierName);

	const {
		options: karoseriCompanyOptions,
		loading: karoseriCompanyLoading,
		onSearch: onKaroseriCompanySearch,
		onPopupScroll: onKaroseriCompanyPopupScroll,
	} = useSelectKaroseriCompany();

	const { options: assetBrandOptions, loading: assetBrandLoading } =
		useSelectAssetBrand(assetTypeId ? Number(assetTypeId) : undefined);

	// Get the selected brand name from the brand ID
	const selectedBrand = assetBrandOptions?.find(
		(option) => option.value === brandId,
	);
	const brandName = selectedBrand?.data?.name;

	const { options: assetOptions, loading: assetLoading } = useSelectAsset(
		brandName || '',
	);

	const {
		options: productOptions,
		loading: productLoading,
		onSearch: onProductSearch,
		onPopupScroll: onProductPopupScroll,
	} = useSelectProduct();

	const { data: existingSimulationResultData } = useGetCalculateSimulation({
		simulationId,
	});

	const { mutateAsync: calculateFiduciaryFee } = useCalculateFiduciaryFee();
	const { mutateAsync: calculateServicingFee } = useCalculateServicingFee();

	// Get Fiduciary Fee
	useEffect(() => {
		const getFiduciaryFee = async () => {
			const amount = await calculateFiduciaryFee({
				isBundle: isBundlingFiduciaryFee ?? false,
				unit: totalUnit,
				requestedOtrAmount: requestedOtrAmount,
			});
			form.setFieldValue(fieldName('fiduciaryFee'), amount);
		};
		if (totalUnit && requestedOtrAmount) {
			getFiduciaryFee();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [totalUnit, isFetched, requestedOtrAmount, isBundlingFiduciaryFee]);

	useEffect(() => {
		const getServicingFee = async () => {
			const amount = await calculateServicingFee({
				provisionPercentage,
				requestedOtrAmount,
				otrSubsidyAmount,
				downPaymentPercentage,
			});
			form.setFieldValue(fieldName('servicingFee'), amount);
		};

		if (
			provisionPercentage &&
			requestedOtrAmount &&
			otrSubsidyAmount &&
			downPaymentPercentage
		) {
			getServicingFee();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		isFetched,
		provisionPercentage,
		requestedOtrAmount,
		otrSubsidyAmount,
		downPaymentPercentage,
		form,
	]);

	useEffect(() => {
		const getPHJMBAmount = async () => {
			const assetId = form.getFieldValue(fieldName('assetId'));
			const amount = await calculatePHJMBAmount({
				assetId,
				assetManufactureYear,
			});
			form.setFieldValue(
				fieldName('phJmbAmount'),
				amount?.data?.phjmbAmount || 0,
			);
		};

		if (
			assetType &&
			form.getFieldValue(fieldName('assetConditionId')) ===
				SIMULATION_ASSET_CONDITION.USED &&
			form.getFieldValue(fieldName('assetType')) ===
				SIMULATION_ASSET_TYPE.PASSENGER &&
			form.getFieldValue(fieldName('assetId')) &&
			assetManufactureYear
		) {
			getPHJMBAmount();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [assetManufactureYear, assetType, form]);

	const handleCalcBack = () => {
		setCalcModalOpen(false);
		navigate('SimulationFacilityListPage', { spfId: spfId! });
	};

	const handleCalcNext = () => {
		form.setFieldValue(fieldName('calculationTypeId'), calcType);
		setCalcModalOpen(false);
	};

	// Map simulationDetailData to MainInformationType
	const handleSubmit = (formData: SimulationFormData) => {
		// Ensure dealerSupplierName is included based on selected dealer supplier
		let dealerSupplierName = formData.dealerSupplierName;
		if (formData.dealerSupplierId && !dealerSupplierName) {
			const selectedDealer = dealerSupplierOptions?.find(
				(option) => option.data.id === formData.dealerSupplierId,
			);
			dealerSupplierName = selectedDealer?.data.name || '';
		}

		// Ensure karoseriCompanyName is included based on selected karoseri company
		let karoseriCompanyName = formData.karoseriCompanyName;
		if (formData.karoseriCompanyId && !karoseriCompanyName) {
			const selectedKaroseri = karoseriCompanyOptions?.find(
				(option) => option.data.id === formData.karoseriCompanyId,
			);
			karoseriCompanyName = selectedKaroseri?.data.name || '';
		}

		// Ensure insuranceGiroTenor is empty array if scheme is not "Bertahap" OR if Main Payment is Full Coverage Cash
		let insuranceGiroTenor = formData.insuranceGiroTenor;
		const mainPaymentId = formData.insurancePaymentId;
		const mainPaymentOptions =
			simulationDetailData?.insurancePaymentOptions || [];
		const selectedMainPayment = mainPaymentOptions.find(
			(opt) => opt.id === mainPaymentId,
		);
		const mainLabel = selectedMainPayment?.value || '';

		const yearsMatch = mainLabel.match(/(\d+)\s*(?:Tahun|Thn|Year)/i);
		const yearsCovered = yearsMatch ? parseInt(yearsMatch[1], 10) : 0;
		const isMainCash =
			mainLabel.toLowerCase().includes('cash') ||
			mainLabel.toLowerCase().includes('tunai');
		const maxTenor = Number(tenorInYear) || 1;

		if (isMainCash && yearsCovered >= maxTenor) {
			insuranceGiroTenor = [];
			// Also clear the others? Or let backend handle valid IDs?
			// Ideally we shouldn't send IDs for hidden fields, but they might be required by API?
			// User didn't specify clearing the IDs, just that the menu doesn't show.
			// But for tenor, user specified "submit []".
		} else if (simulationDetailData?.paymentSchemeInsuranceGiroOptions) {
			const schemeId = formData.insurancePaymentSchemeGiroTypeId;
			const selectedScheme =
				simulationDetailData.paymentSchemeInsuranceGiroOptions.find(
					(opt) => opt.id === schemeId,
				);
			const isBertahap = selectedScheme?.value
				?.toLowerCase()
				.includes('bertahap');

			// Also check if valid startYear <= maxTenor (from MTFClause Logic)
			// But simpler: if !isBertahap, clear it.
			if (!isBertahap) {
				insuranceGiroTenor = [];
			}
		}

		const payload = {
			...formData,
			insuranceGiroTenor,
			dealerSupplierName,
			karoseriCompanyName,
			assetTypeId: assetTypeId || '',
		};
		if (userData && userData.groupName === 'FCOH' && simulationId) {
			editSimulationProduct({
				simulationId: simulationId,
				productId: payload.productId,
				productOffering: payload.productOffering,
				assetId: payload.assetId,
				assetTypeId: payload.assetTypeId,
				assetBrandId: payload.assetBrandId,
			})
				.then((value) => {
					message.success(value.data.message);
				})
				.catch((e: unknown) => {
					message.error('Gagal menghitung simulasi.');
					console.error(e);
				});
		} else {
			calculateSimulation(payload as CalculateSimulationRequest)
				.then((value) => {
					message.success('Simulasi berhasil dihitung!');
					setSimulationResult(value.data);
					setSimulationFormValues({
						...payload,
						spfId: spfId!,
						facilityId: facilityId!,
					});
				})
				.catch((e: unknown) => {
					message.error('Gagal menghitung simulasi.');
					console.error(e);
				});
		}
	};

	const onFormFinish = (formData: SimulationFormData) => {
		setPendingFormData({ ...formData, calculationTypeId: calcTypeInForm });
		setConfirmOpen(true);
	};

	const handleConfirmSubmit = () => {
		if (pendingFormData) {
			handleSubmit(pendingFormData);
		}
		setConfirmOpen(false);
		setPendingFormData(null);
	};

	const handleCancelSubmit = () => {
		setConfirmOpen(false);
		setPendingFormData(null);
	};

	// OTR Heavy Equipment
	const onChangeOtrHeHe = (value: string | number | null) => {
		// Ensure value is a number, defaulting to 0 if null/undefined/empty string
		const numericValue = Number(value ?? 0);

		// Ensure otrHeOth is a number, defaulting to 0 if null/undefined/empty string
		// It's crucial to access otrHeOth in a way that provides its current numerical value.
		// This might involve reading directly from form state, or ensuring it's a state variable
		// that's always initialized.
		const numericOtrHeOth = Number(otrHeOth ?? 0);

		form.setFieldValue(
			fieldName('requestedOtrAmount'),
			numericValue + numericOtrHeOth,
		);
	};

	// OTR Others
	const onChangeOtrHeOth = (value: string | number | null) => {
		// Ensure value is a number, defaulting to 0 if null/undefined/empty string
		const numericValue = Number(value ?? 0);

		// Ensure otrHeHe is a number, defaulting to 0 if null/undefined/empty string
		const numericOtrHeHe = Number(otrHeHe ?? 0);

		form.setFieldValue(
			fieldName('requestedOtrAmount'),
			numericValue + numericOtrHeHe,
		);
	};

	// OTR Chasis
	const onChangeOtrChasKarChas = (value: string | number | null) => {
		// Ensure value is a number, defaulting to 0 if null/undefined/empty string
		const numericValue = Number(value ?? 0);

		// Ensure otrHeOth is a number, defaulting to 0 if null/undefined/empty string
		// It's crucial to access otrHeOth in a way that provides its current numerical value.
		// This might involve reading directly from form state, or ensuring it's a state variable
		// that's always initialized.
		const numericOtrChasKarKar = Number(otrChasKarKar ?? 0);

		form.setFieldValue(
			fieldName('requestedOtrAmount'),
			numericValue + numericOtrChasKarKar,
		);
	};

	// OTR Others
	const onChangeOtrChasKarKar = (value: string | number | null) => {
		// Ensure value is a number, defaulting to 0 if null/undefined/empty string
		const numericValue = Number(value ?? 0);

		// Ensure otrHeHe is a number, defaulting to 0 if null/undefined/empty string
		const numericOtrChasKarChas = Number(otrChasKarChas ?? 0);

		form.setFieldValue(
			fieldName('requestedOtrAmount'),
			numericValue + numericOtrChasKarChas,
		);
	};

	const karoseriDownPayment = useMemo(
		() => (karoseriCalculationUnitPrice * karoseriCalculationDP) / 100,
		[karoseriCalculationUnitPrice, karoseriCalculationDP],
	);

	const chasisDownPayment = useMemo(
		() => (chasisCalculationUnitPrice * chasisCalculationDP) / 100,
		[chasisCalculationUnitPrice, chasisCalculationDP],
	);

	// Set form values when simulationDetailData changes
	useEffect(() => {
		if (simulationDetailData) {
			form.resetFields();
			form.setFieldsValue({
				...mapSimulationDetailToFormValues(simulationDetailData),
				spfId,
				calculationTypeId: Number(
					simulationDetailData?.calculationType?.id ?? 1,
				),
			});
		}
	}, [form, simulationDetailData, spfId]);

	// MODIFICATION: This new useEffect handles setting the address and phone fields on initial load.
	useEffect(() => {
		// Ensure we have the necessary data to proceed.
		if (
			!simulationDetailData ||
			!dealerSupplierOptions ||
			dealerSupplierOptions.length === 0
		) {
			return;
		}

		// 1. Handle Dealer/Supplier Address and Phone
		const defaultDealerId = form.getFieldValue(fieldName('dealerSupplierId'));
		if (defaultDealerId) {
			const selectedDealer = dealerSupplierOptions.find(
				(opt) => opt.data.id === defaultDealerId,
			);
			if (selectedDealer) {
				form.setFieldValue(
					fieldName('dealerSupplierAddress'),
					selectedDealer.data.address,
				);
				form.setFieldValue(
					fieldName('dealerSupplierPhoneNo'),
					selectedDealer.data.phoneNo,
				);
			}
		}

		// 2. Handle Karoseri Company Address and Phone
		const defaultKaroseriId = form.getFieldValue(
			fieldName('karoseriCompanyId'),
		);
		if (defaultKaroseriId) {
			const selectedKaroseri = dealerSupplierOptions.find(
				(opt) => opt.data.id === defaultKaroseriId,
			);
			if (selectedKaroseri) {
				form.setFieldValue(
					fieldName('karoseriCompanyAddress'),
					selectedKaroseri.data.address,
				);
				form.setFieldValue(
					fieldName('karoseriCompanyPhoneNo'),
					selectedKaroseri.data.phoneNo,
				);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!facilityIdInForm && facilityId) {
			form.setFieldValue(fieldName('facilityId'), facilityId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [facilityId, facilityIdInForm]);

	// Get Existing Calculation Result if showResult is true and simulation Calculation exists
	useEffect(() => {
		if (showResult && existingSimulationResultData) {
			setSimulationResult(existingSimulationResultData);
		}
	}, [existingSimulationResultData, showResult]);

	const validTenorInYearOptions = useMemo(() => {
		if (!simulationDetailData) {
			return [];
		}
		if (assetConditionInForm === SIMULATION_ASSET_CONDITION.USED) {
			if (
				assetTypeInForm === SIMULATION_ASSET_TYPE.PASSENGER ||
				assetTypeInForm === SIMULATION_ASSET_TYPE.COMMERCIAL
			) {
				return simulationDetailData.tenorInYearOptions.filter(
					(item) => Number(item.id) <= 5,
				);
			} else if (assetTypeInForm === SIMULATION_ASSET_TYPE.HEAVY_EQUIPMENT) {
				return simulationDetailData.tenorInYearOptions.filter(
					(item) => Number(item.id) <= 3,
				);
			}
		}
		// Fallback: return original options if no specific filtering applies
		return simulationDetailData.tenorInYearOptions;
	}, [assetConditionInForm, assetTypeInForm, simulationDetailData]);

	useEffect(() => {
		setCalcType(calcTypeInForm);
	}, [calcTypeInForm]);

	// Loading
	if (!isFetched) {
		return (
			<div
				style={{
					width: '100%',
					height: '100%',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			>
				<Spin />
			</div>
		);
	}

	if ((error && isFetched) || !simulationDetailData) {
		return (
			<div
				style={{
					width: '100%',
					height: '100%',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					flexDirection: 'column',
				}}
			>
				<InfoCircleFilled />
				Error loading data. Please contact
			</div>
		);
	}

	if (!simulationDetailData) {
		return (
			<div
				style={{
					width: '100%',
					height: '100%',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			>
				<Spin />
			</div>
		);
	}
	// Has result or should show calculation result
	if (
		(simulationResult && simulationFormValues) ||
		(shouldShowResult && showCalculation)
	)
		return (
			<>
				<NAPNJFModal {...napNJFModal} />
				<NAPJFModal {...napJFModal} />
				<div
					style={{
						display: 'flex',
						gap: 10,
						marginBottom: 30,
					}}
				>
					{typeof onBack === 'function' && (
						<button
							style={{
								display: 'flex',
								height: 44,
								width: 44,
								fontSize: 24,
								border: 'none',
								alignItems: 'center',
								justifyContent: 'center',
								background: '#1557BC',
								color: 'white',
								cursor: 'pointer',
							}}
							onClick={() => {
								if (shouldShowResult) {
									setShouldShowResult(false);
									setShowCalculation(false);
								} else if (onBack) {
									onBack();
								}
							}}
						>
							<ArrowLeftOutlined />
						</button>
					)}
					<div style={{ flex: 1 }}>
						<FleetTitle>Main Informations</FleetTitle>
					</div>
				</div>
				<DataRow
					items={[
						[
							[
								'Nama Customer',
								simulationDetailData?.customerName ? (
									<a
										onClick={(e) => {
											e.preventDefault();
											openCustomerDetail(simulationDetailData.customerId);
										}}
										style={{ color: '#1557BC', fontWeight: 500 }}
									>
										{simulationDetailData.customerName}
									</a>
								) : (
									'-'
								),
							],
							['No.MKL', simulationDetailData?.mklNo ?? '-'],
						],
						[
							[
								'Group Customer BMPP',
								simulationDetailData?.bmppGroupName ? (
									<a
										onClick={(e) => {
											e.preventDefault();
											openCustomerGroupDetail(simulationDetailData.bmppGroupId);
										}}
										style={{ color: '#1557BC', fontWeight: 500 }}
									>
										{simulationDetailData.bmppGroupName}
									</a>
								) : (
									'-'
								),
							],
							['Sumber Order', simulationDetailData?.orderSource ?? '-'],
						],
						[
							['Kelas Industri', simulationDetailData?.industryClass ?? '-'],
							[
								'Portofolio Guideline',
								simulationDetailData?.portfolioGuideline ?? '-',
							],
						],
						[
							['Ultimate Owner', simulationDetailData?.ultimateOwner ?? '-'],
							[
								'Summary Risalah',
								simulationDetailData?.resolutionFile ? (
									<a
										href="#"
										onClick={(e) => {
											e.preventDefault();
											openPreviewModal(simulationDetailData?.resolutionFile);
										}}
									>
										View Dokumen
									</a>
								) : (
									'-'
								),
							],
						],
						[
							[
								'Summary NAP',
								simulationDetailData?.plafonTypeId ? (
									<a
										href="#"
										onClick={(e) => {
											e.preventDefault();
											if (!simulationDetailData?.prospectId) return;

											if (simulationDetailData?.plafonTypeId === 1) {
												napNJFModal.open({
													prospectId: simulationDetailData?.prospectId,
												});
											}

											if (simulationDetailData?.plafonTypeId === 2) {
												napJFModal.open({
													prospectId: simulationDetailData?.prospectId,
												});
											}
										}}
									>
										View Dokumen
									</a>
								) : (
									'-'
								),
							],
						],
					]}
					style={{ marginBottom: 20 }}
				/>

				{simulationResult && (
					<SimulationPagePreview
						simulationResult={simulationResult}
						simulationRequest={simulationFormValues}
						simulationId={simulationId}
						viewOnly={viewOnly}
					/>
				)}
			</>
		);

	return (
		<>
			<NAPNJFModal {...napNJFModal} />
			<NAPJFModal {...napJFModal} />
			{/* Calculation Type Modal */}
			<FleetModal
				open={calcModalOpen}
				closable={false}
				maskClosable={false}
				footer={null}
				width={420}
				style={{
					padding: '36px 32px 24px 32px',
					borderRadius: 12,
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					width: '100%',
				}}
			>
				<div style={{ display: 'flex', justifyContent: 'center' }}>
					<InfoCircleFilled
						style={{
							fontSize: 48,
							color: '#1557BC',
							marginBottom: 16,
						}}
					/>
				</div>

				<div
					style={{
						fontWeight: 600,
						fontSize: 20,
						textAlign: 'center',
						marginBottom: 24,
					}}
				>
					Silakan pilih jenis perhitungan yang ingin Anda gunakan
				</div>
				<div
					style={{
						display: 'flex',
						gap: 32,
						marginBottom: 32,
					}}
				>
					{calculationTypeOptions.map((opt) => (
						<label
							key={opt.value}
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: 8,
								fontWeight: 500,
								fontSize: 16,
								whiteSpace: 'nowrap', // Changed from 'none' to 'nowrap'
								textWrap: 'nowrap',
								color: calcType === opt.value ? '#1557BC' : '#222',
							}}
						>
							<input
								type="radio"
								checked={calcType === opt.value}
								onChange={() => setCalcType(opt.value)}
							/>
							{opt.label}
						</label>
					))}
				</div>
				<div
					style={{
						display: 'flex',
						justifyContent: 'center',
						gap: 16,
						width: '100%',
						marginBottom: 24,
					}}
				>
					<Button
						onClick={handleCalcBack}
						type="default"
					>
						BACK
					</Button>
					<Button
						onClick={handleCalcNext}
						type="primary"
					>
						NEXT
					</Button>
				</div>
				<div
					style={{
						position: 'absolute',
						left: 0,
						bottom: 0,
						width: '100%',
						height: 8,
						display: 'flex',
					}}
				>
					<div
						style={{ background: '#1557BC', height: '100%', width: '85%' }}
					/>
					<div
						style={{ background: '#FFB300', height: '100%', width: '15%' }}
					/>
				</div>
			</FleetModal>
			<div style={{ padding: 24, position: 'relative' }}>
				<div
					style={{
						display: 'flex',
						gap: 10,
						marginBottom: 20,
						width: '100%',
					}}
				>
					<div>
						<button
							style={{
								display: 'flex',
								height: 44,
								width: 44,
								fontSize: 24,
								border: 'none',
								alignItems: 'center',
								justifyContent: 'center',
								background: '#1557BC',
								color: 'white',
								cursor: 'pointer',
							}}
							onClick={() => {
								if (shouldShowResult) {
									setShouldShowResult(false);
									setShowCalculation(false);
								} else if (typeof onBack === 'function') {
									onBack();
								} else {
									navigate('FRMSimulationListPage');
								}
							}}
						>
							<ArrowLeftOutlined />
						</button>
					</div>
					<FleetTitle style={{ width: '100%' }}>Main Information</FleetTitle>
				</div>
				{/* Replace <MainInformation ... /> with custom info section */}
				<DataRow
					items={[
						[
							[
								'Nama Customer',
								simulationDetailData?.customerName ? (
									<a
										onClick={(e) => {
											e.preventDefault();
											openCustomerDetail(simulationDetailData.customerId);
										}}
										style={{ color: '#1557BC', fontWeight: 500 }}
									>
										{simulationDetailData.customerName}
									</a>
								) : (
									'-'
								),
							],
							['No.MKL', simulationDetailData?.mklNo ?? '-'],
						],
						[
							[
								'Group Customer BMPP',
								simulationDetailData?.bmppGroupName ? (
									<a
										onClick={(e) => {
											e.preventDefault();
											openCustomerGroupDetail(simulationDetailData.bmppGroupId);
										}}
										style={{ color: '#1557BC', fontWeight: 500 }}
									>
										{simulationDetailData.bmppGroupName}
									</a>
								) : (
									'-'
								),
							],
							['Sumber Order', simulationDetailData?.orderSource ?? '-'],
						],
						[
							['Kelas Industri', simulationDetailData?.industryClass ?? '-'],
							[
								'Portofolio Guideline',
								simulationDetailData?.portfolioGuideline ?? '-',
							],
						],
						[
							['Ultimate Owner', simulationDetailData?.ultimateOwner ?? '-'],
							[
								'Summary Risalah',
								simulationDetailData?.resolutionFile ? (
									<a
										href="#"
										onClick={(e) => {
											e.preventDefault();
											openPreviewModal(simulationDetailData?.resolutionFile);
										}}
									>
										View Dokumen
									</a>
								) : (
									'-'
								),
							],
						],
						[
							[
								'Summary NAP',
								simulationDetailData?.plafonTypeId ? (
									<a
										href="#"
										onClick={(e) => {
											e.preventDefault();
											if (!simulationDetailData?.prospectId) return;

											if (simulationDetailData?.plafonTypeId === 1) {
												napNJFModal.open({
													prospectId: simulationDetailData?.prospectId,
												});
											}

											if (simulationDetailData?.plafonTypeId === 2) {
												napJFModal.open({
													prospectId: simulationDetailData?.prospectId,
												});
											}
										}}
									>
										View Dokumen
									</a>
								) : (
									'-'
								),
							],
						],
					]}
					style={{ marginBottom: 20 }}
				/>
				{/* Simulasi Kredit Section */}
				<div>
					<FleetTitle>Simulasi Kredit</FleetTitle>
					<DataRow
						items={[
							[
								['Tipe Customer', 'Existing Customer'],
								['Jenis Customer', 'Badan Usaha'],
							],
							[
								[
									'Jenis Perhitungan',
									enumToLabel(
										SIMULATION_CALCULATION_TYPE_OPTIONS[
											calculationTypeIdInForm ?? calcType
										],
									),
								],
							],
						]}
						col={12}
						style={{ background: 'transparent', marginBottom: 20 }}
					/>
				</div>
				{/* End Simulasi Kredit Section */}
				{/* Accordion Section */}
				<Form
					{...ANTD_DEFAULT_PROPS.FORM}
					form={form}
					layout="horizontal"
					onFinish={onFormFinish}
					scrollToFirstError
					disabled={
						viewOnly ||
						showResult ||
						(userData && userData.groupName === 'FCOH')
					}
					// Add labelCol and wrapperCol here for consistent width
				>
					{/* Ensure calculationTypeId is always registered in the form */}
					<Form.Item
						name={fieldName('calculationTypeId')}
						style={{ display: 'none' }}
					>
						<Input type="hidden" />
					</Form.Item>
					<Form.Item
						name={fieldName('spfId')}
						style={{ display: 'none' }}
					>
						<Input type="hidden" />
					</Form.Item>
					<Form.Item
						name={fieldName('facilityId')}
						style={{ display: 'none' }}
					>
						<Input type="hidden" />
					</Form.Item>
					{/* Split PO Radio Button - moved inside main Form */}
					{assetType === SIMULATION_ASSET_TYPE.HEAVY_EQUIPMENT ||
					assetType === SIMULATION_ASSET_TYPE.COMMERCIAL ? (
						<Row gutter={40}>
							<Col span={24}>
								<Form.Item
									name={fieldName('isSplitPo')}
									label="Apakah pengajuan merupakan split PO?"
									rules={[
										{ required: true, message: 'Wajib pilih salah satu' },
									]}
								>
									<Radio.Group>
										<Radio value={true}>Ya</Radio>
										<Radio value={false}>Tidak</Radio>
									</Radio.Group>
								</Form.Item>
							</Col>
						</Row>
					) : null}

					<Divider />
					<Collapse
						defaultActiveKey={[
							'calc-dealer',
							'asset-information',
							'product-scheme',
							'calculation-simulation',
							'insurance-section',
						]}
					>
						<Collapse.Panel
							header="Informasi Dealer dan Supplier"
							key="calc-dealer"
						>
							{/* Your existing Dealer/Supplier Section starts here */}
							<Row gutter={40}>
								<Col span={12}>
									<Form.Item
										name={fieldName('dealerSupplierId')}
										label="Dealer/Supplier"
										rules={[
											{
												required: true,
												message: 'Dealer/Supplier wajib diisi',
											},
										]}
									>
										<Select
											showSearch
											options={dealerSupplierOptions}
											loading={dealerSupplierLoading}
											onSearch={onDealerSupplierSearch}
											onPopupScroll={onDealerSupplierPopupScroll}
											filterOption={false}
											style={{ width: '100%' }}
											onChange={(value) => {
												const dealer = dealerSupplierOptions?.find(
													(d) => d.data.id === value,
												);
												form.setFieldValue(
													fieldName('dealerSupplierName'),
													dealer?.data.name,
												);
												form.setFieldValue(
													fieldName('dealerSupplierAddress'),
													dealer?.data.address,
												);
												form.setFieldValue(
													fieldName('dealerSupplierPhoneNo'),
													dealer?.data.phoneNo,
												);
											}}
										/>
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item
										name={fieldName('dealerSupplierPhoneNo')}
										label="No.Telepon Dealer/Supplier"
									>
										<Input style={{ width: '100%' }} />
									</Form.Item>
								</Col>
							</Row>
							{/* Your existing Dealer/Supplier Section ends here */}
							{/* Start of new "Informasi Karoseri" section */}
							<Divider />
							<Row gutter={40}>
								<Col span={24}>
									<Form.Item
										name={fieldName('dealerSupplierAddress')}
										label="Alamat Dealer"
										rules={[
											{
												required: true,
												message: 'Alamat Dealer wajib diisi',
											},
										]}
										labelCol={{ span: 6 }}
									>
										<Input style={{ width: '100%' }} />
									</Form.Item>
								</Col>
							</Row>
							<Divider />

							<Row gutter={40}>
								<Col span={12}>
									<Form.Item
										name={fieldName('dealerSupplierPicName')}
										label="PIC Dealer/Supplier"
										rules={[
											{
												required: true,
												message: 'PIC Dealer/Supplier wajib diisi',
											},
										]}
									>
										<Input style={{ width: '100%' }} />
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item
										name={fieldName('dealerSupplierPicPhoneNo')}
										label="No. Telepon PIC Dealer/Supplier"
										rules={[
											{
												required: true,
												message: 'No. Telepon PIC Dealer/Supplier wajib diisi',
											},
											rule.strNumber(),
											rule.phoneNumber(),
										]}
									>
										<Input style={{ width: '100%' }} />
									</Form.Item>
								</Col>
							</Row>

							<Divider />

							{isSplitPo && (
								<>
									<FleetTitle level={5}>Informasi Karoseri</FleetTitle>
									<Row gutter={40}>
										{/* Nama Perusahaan Karoseri */}
										<Col span={12}>
											<Form.Item
												name={fieldName('karoseriCompanyId')}
												label="Nama Perusahaan Karoseri"
												rules={[
													{
														required: true,
														message: 'Nama Perusahaan Karoseri wajib diisi',
													},
												]}
											>
												{/* <Select
													showSearch
													options={karoseriCompanyOptions}
													loading={karoseriCompanyLoading}
													onChange={(value) => {
														const karoseri = karoseriCompanyOptions?.find(
															(d) => d.data.id === value,
														);
														form.setFieldValue(
															fieldName('karoseriCompanyName'),
															karoseri?.data.name,
														);
														form.setFieldValue(
															fieldName('karoseriCompanyAddress'),
															karoseri?.data.address,
														);
														form.setFieldValue(
															fieldName('karoseriCompanyPhoneNo'),
															karoseri?.data.phoneNo,
														);
													}}
												/> */}
												<Select
													showSearch
													options={karoseriCompanyOptions}
													loading={karoseriCompanyLoading}
													onSearch={onKaroseriCompanySearch}
													onPopupScroll={onKaroseriCompanyPopupScroll}
													filterOption={false}
													style={{ width: '100%' }}
													onChange={(value) => {
														const karoseri = karoseriCompanyOptions?.find(
															(d) => d.data.id === value,
														);
														form.setFieldValue(
															fieldName('karoseriCompanyName'),
															karoseri?.data.name,
														);
														form.setFieldValue(
															fieldName('karoseriCompanyAddress'),
															karoseri?.data.address,
														);
														form.setFieldValue(
															fieldName('karoseriCompanyPhoneNo'),
															karoseri?.data.phoneNo,
														);
													}}
												/>
											</Form.Item>
										</Col>
										{/* No. Telepon Perusahaan Karoseri */}
										<Col span={12}>
											<Form.Item
												name={fieldName('karoseriCompanyPhoneNo')}
												label="No. Telepon Perusahaan Karoseri"
												rules={[
													{
														required: true,
														message:
															'No. Telepon Perusahaan Karoseri wajib diisi',
													},
													rule.strNumber(),
													rule.phoneNumber(),
												]}
											>
												<Input style={{ width: '100%' }} />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={40}>
										<Col span={24}>
											<Form.Item
												name={fieldName('karoseriCompanyAddress')}
												label="Alamat Perusahaan Karoseri"
												rules={[
													{
														required: true,
														message: 'Alamat Perusahaan Karoseri wajib diisi',
													},
												]}
												labelCol={{ span: 6 }}
											>
												<Input style={{ width: '100%' }} />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={40}>
										{/* PIC Perusahaan Karoseri */}
										<Col span={12}>
											<Form.Item
												name={fieldName('karoseriCompanyPicName')}
												label="PIC Perusahaan Karoseri"
												rules={[
													{
														required: true,
														message: 'PIC Perusahaan Karoseri wajib diisi',
													},
												]}
											>
												<Input style={{ width: '100%' }} />
											</Form.Item>
										</Col>
										{/* No. Telepon PIC Perusahaan Karoseri */}
										<Col span={12}>
											<Form.Item
												name={fieldName(['karoseriCompanyPicPhoneNo'])}
												label="No. Telepon PIC Perusahaan Karoseri"
												rules={[
													{
														required: true,
														message:
															'No. Telepon PIC Perusahaan Karoseri wajib diisi',
													},
													rule.strNumber(),
													rule.phoneNumber(),
												]}
											>
												<Input style={{ width: '100%' }} />
											</Form.Item>
										</Col>
									</Row>
								</>
							)}

							{/* End of new "Informasi Karoseri" section */}

							<Alert
								message="Apabila Nama Dealer/Supplier dan Nama Perusahaan Karoseri tidak ada, silakan lakukan aktivasi atau penambahan dealer di estar."
								type="warning"
								showIcon
								className="custom-alert-info"
							/>
						</Collapse.Panel>
						<Collapse.Panel
							header="Informasi Asset"
							key="asset-information"
						>
							<Form
								{...ANTD_DEFAULT_PROPS.FORM}
								form={form}
								layout="horizontal"
								onFinish={onFormFinish}
								// labelCol and wrapperCol typically not used with vertical layout for Form.Items
								// Instead, ensure inner components like Select/Input fill the Col
							>
								<Row gutter={40}>
									<Col span={12}>
										<Form.Item
											name={fieldName('assetType')}
											label="Jenis Asset"
											rules={[{ required: true }]}
										>
											<Input
												value={simulationDetailData.assetType}
												disabled
												readOnly
												style={{ width: '100%' }}
											/>
										</Form.Item>
									</Col>
									<Col span={12}>
										<Form.Item
											name={fieldName('assetConditionId')}
											label="Kondisi Aset"
											rules={[{ required: true }]}
										>
											<Select
												options={mapOptionsToSelect(
													simulationDetailData?.assetConditionOptions,
												)}
												style={{ width: '100%' }}
											/>
										</Form.Item>
									</Col>
								</Row>
								<Divider />

								<Row gutter={40}>
									<Col span={12}>
										<Form.Item
											name={fieldName('assetUsageId')}
											label="Penggunaan Aset"
											rules={[{ required: true }]}
										>
											<Select
												options={mapOptionsToSelect(
													simulationDetailData?.assetUsageOptions,
												)}
												style={{ width: '100%' }}
											/>
										</Form.Item>
									</Col>
									<Col span={12}>
										<Form.Item
											label="Category"
											rules={[{ required: true }]}
											name={fieldName('assetCategory')}
										>
											<Input
												disabled
												readOnly
												style={{ width: '100%' }}
											/>
										</Form.Item>
									</Col>
								</Row>
								<Divider />

								<Row gutter={40}>
									<Col span={12}>
										<Form.Item
											name={fieldName('assetBrandId')}
											label="Brand"
											rules={[{ required: true }]}
										>
											<Select
												showSearch
												options={assetBrandOptions}
												loading={assetBrandLoading}
												style={{ width: '100%' }}
												disabled={
													viewOnly ||
													!(
														userData?.groupName === 'FCOH' ||
														userData?.groupName === 'FRM'
													)
												}
											/>
										</Form.Item>
									</Col>
									<Col span={12}>
										<Form.Item
											name={fieldName('assetId')}
											label="Deskripsi Aset"
											rules={[{ required: true }]}
										>
											<Select
												showSearch
												options={assetOptions}
												loading={assetLoading}
												onChange={(_, option) => {
													if (
														option &&
														'data' in option &&
														option.data?.categoryId
													) {
														form.setFieldValue(
															fieldName('assetCategoryId'),
															option.data.categoryId,
														);
														form.setFieldValue(
															fieldName('assetCategory'),
															option.data.categoryId,
														);
														form.setFieldValue(
															fieldName('assetTypeId'),
															option.data.categoryId,
														);
													}
												}}
												style={{ width: '100%' }}
												disabled={
													viewOnly ||
													!(
														userData?.groupName === 'FCOH' ||
														userData?.groupName === 'FRM'
													)
												}
											/>
										</Form.Item>
									</Col>
								</Row>
								<Divider />

								<Row gutter={40}>
									<Col span={12}>
										<Form.Item
											name={fieldName('assetManufactureYear')}
											label="Tahun Pembuatan"
											rules={[
												{
													required: true,
													message: 'Tahun Pembuatan wajib diisi',
												},
												() => ({
													validator(_, value) {
														if (!value) {
															return Promise.resolve();
														}

														const currentYear = dayjs().year();
														const assetConditionInForm = form.getFieldValue(
															fieldName('assetConditionId'),
														);

														const tenorInYears = form.getFieldValue(
															fieldName('tenorInYear'),
														);

														if (
															assetConditionInForm ===
															SIMULATION_ASSET_CONDITION.NEW
														) {
															const formattedValue = dayjs(value).year();
															// For new vehicles, year cannot exceed current year
															if (formattedValue > currentYear) {
																return Promise.reject(
																	'Tahun pembuatan untuk kendaraan baru tidak boleh melebihi tahun saat ini.',
																);
															}
															return Promise.resolve();
														}

														if (
															assetConditionInForm ===
															SIMULATION_ASSET_CONDITION.USED
														) {
															if (
																assetTypeInForm ===
																	SIMULATION_ASSET_TYPE.PASSENGER ||
																assetTypeInForm ===
																	SIMULATION_ASSET_TYPE.COMMERCIAL
															) {
																// Max tenor 5 years
																const maxTenor = 5;
																if (tenorInYears > maxTenor) {
																	return Promise.reject(
																		`Tenor untuk ${enumToLabel(
																			// @ts-expect-error type
																			SIMULATION_ASSET_TYPE[assetTypeInForm],
																		)} bekas tidak boleh lebih dari ${maxTenor} tahun.`,
																	);
																}

																// 10 years at the end of tenor
																const minManufactureYear =
																	currentYear - (10 - tenorInYears);
																if (value < minManufactureYear) {
																	return Promise.reject(
																		`Tahun pembuatan minimal adalah ${minManufactureYear}.`,
																	);
																}
															}

															if (
																assetTypeInForm ===
																SIMULATION_ASSET_TYPE.HEAVY_EQUIPMENT
															) {
																// Max tenor 3 years
																const maxTenor = 3;

																if (tenorInYears > maxTenor) {
																	return Promise.reject(
																		`Tenor untuk Heavy Equipment bekas tidak boleh lebih dari ${maxTenor} tahun.`,
																	);
																}

																// 8 years at the end of tenor
																const minManufactureYear =
																	currentYear - (8 - tenorInYears);
																if (value < minManufactureYear) {
																	return Promise.reject(
																		`Tahun pembuatan minimal adalah ${minManufactureYear}.`,
																	);
																}
															}
														}

														return Promise.resolve();
													},
												}),
											]}
											getValueProps={(value) => ({
												value: value ? dayjs().year(value) : null,
											})}
										>
											<DatePicker
												picker="year"
												style={{ width: '100%' }}
												disabledDate={(date) => {
													// Disable years after current year
													return date && date.year() > dayjs().year();
												}}
												onChange={(date) => {
													if (date) {
														form.setFieldValue(
															fieldName('assetManufactureYear'),
															date.year(),
														);
													} else {
														form.setFieldValue(
															fieldName('assetManufactureYear'),
															null,
														);
													}
												}}
											/>
										</Form.Item>
									</Col>
									<Col span={12}>
										<Form.Item
											name={fieldName('otrTypeId')}
											label="Tipe OTR"
											rules={[{ required: true }]}
										>
											<Select
												options={mapOptionsToSelect(
													simulationDetailData?.otrTypeOptions,
												)}
												style={{ width: '100%' }}
											/>
										</Form.Item>
									</Col>
								</Row>
							</Form>
						</Collapse.Panel>
						<Collapse.Panel
							header="Skema Product"
							key="product-scheme"
						>
							<Form
								{...ANTD_DEFAULT_PROPS.FORM}
								form={form}
								layout="horizontal" // Note: This internal form has vertical layout
								onFinish={onFormFinish}
							>
								<Row gutter={40}>
									<Col span={12}>
										<Form.Item
											name={fieldName('financingProductId')}
											label="Product Pembiayaan"
											rules={[{ required: true }]}
										>
											<Select
												options={mapOptionsToSelect(
													simulationDetailData?.financingProductOptions,
												)}
												style={{ width: '100%' }}
											/>
										</Form.Item>
									</Col>
									<Col span={12}>
										<Form.Item
											name={fieldName('productId')}
											label="Product"
										>
											<Select
												showSearch
												options={productOptions}
												loading={productLoading}
												onSearch={onProductSearch}
												onPopupScroll={onProductPopupScroll}
												filterOption={false}
												style={{ width: '100%' }}
												disabled={
													viewOnly ||
													!(
														userData?.groupName === 'FCOH' ||
														userData?.groupName === 'FRM'
													)
												}
											/>
										</Form.Item>
									</Col>
								</Row>
								<Divider />
								<Row gutter={40}>
									<Col span={12}>
										<Form.Item
											name={fieldName('productOffering')}
											label="Product Offering"
										>
											<Input
												style={{ width: '100%' }}
												disabled={
													viewOnly ||
													!(
														userData?.groupName === 'FCOH' ||
														userData?.groupName === 'FRM'
													)
												}
											/>
										</Form.Item>
									</Col>
									<Col span={12}>
										<Form.Item
											name={fieldName('installmentTypeId')}
											label="Tipe Angsuran"
											rules={[{ required: true }]}
										>
											<Select
												options={mapOptionsToSelect(
													simulationDetailData?.installmentTypeOptions,
												)}
												style={{ width: '100%' }}
											/>
										</Form.Item>
									</Col>
								</Row>
							</Form>
						</Collapse.Panel>
						<Collapse.Panel
							header="Simulasi Perhitungan"
							key="calculation-simulation"
						>
							<div className="form-section-group">
								{/* Start Of Split Mode */}
								{isSplitPo && (
									<>
										<span style={{ fontSize: 24, fontWeight: 'bold' }}>
											Perhitungan Karoseri
										</span>
										<Row gutter={40}>
											<Col span={12}>
												<Form.Item
													name={fieldName(['karoseriCalculation', 'type'])}
													label="Jenis Karoseri"
													rules={[
														{
															required: true,
															message: 'Jenis Karoseri wajib diisi',
														},
													]}
												>
													<Input style={{ width: '100%' }} />
												</Form.Item>
											</Col>
											<Col span={12}>
												<Form.Item
													name={fieldName([
														'karoseriCalculation',
														'bpkbOwnerName',
													])}
													label="Atas Nama BPKB"
													rules={[{ required: true }]}
												>
													<Input style={{ width: '100%' }} />
												</Form.Item>
											</Col>
										</Row>

										<Divider />

										<Row gutter={40}>
											<Col span={12}>
												<Form.Item
													name={fieldName(['karoseriCalculation', 'unitPrice'])}
													label="Harga Barang/Unit"
													rules={[
														{
															required: true,
															message: 'Harga Barang wajib diisi',
														},
													]}
												>
													<InputNumber
														style={{ width: '100%' }}
														formatter={(value) =>
															value
																? value
																		.toString()
																		.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
																: ''
														}
														parser={(value) =>
															value ? value.replace(/\D/g, '') : ''
														}
													/>
												</Form.Item>
											</Col>
											<Col span={12}>
												<Form.Item
													// name={fieldName([ // No need to send total price
													// 	'karoseriCalculation',
													// 	'totalPrice',
													// ])}
													label="Total Harga"
												>
													<InputNumber
														formatter={(value) =>
															value
																? value
																		.toString()
																		.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
																: ''
														}
														readOnly
														disabled
														value={karoseriCalculationUnitPrice * totalUnit}
														style={{ width: '100%' }}
													/>
												</Form.Item>
											</Col>
										</Row>

										<Divider />

										<Row gutter={40}>
											<Col span={12}>
												<Row gutter={12}>
													<Col span={12}>
														<Form.Item
															name={fieldName([
																'karoseriCalculation',
																'downPaymentPercentage',
															])}
															label="Down Payment"
															rules={[
																{
																	required: true,
																	message: 'Harga Barang wajib diisi',
																},
																{
																	validator(_, value) {
																		if (
																			value <
																			simulationDetailData.minimumDownPaymentPercentage
																		) {
																			return Promise.reject(
																				'Net Down Payment yang Anda inputkan berada di bawah ketentuan yang berlaku. Silakan perbarui Nett Down Payment sesuai ketentuan.',
																			);
																		}
																		return Promise.resolve();
																	},
																},
															]}
														>
															<InputNumber
																style={{ width: '100%' }}
																min={0}
																max={100}
																addonAfter="%"
															/>
														</Form.Item>
													</Col>
													<Col span={12}>
														<InputNumber
															value={karoseriDownPayment}
															formatter={(value) =>
																value
																	? value
																			.toString()
																			.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
																	: ''
															}
															disabled
															readOnly
															style={{ width: '50%' }}
														/>
													</Col>
												</Row>
											</Col>
										</Row>

										<Divider />

										<span style={{ fontSize: 24, fontWeight: 'bold' }}>
											Perhitungan Chasis
										</span>

										<Row gutter={40}>
											<Col span={12}>
												<Form.Item
													name={fieldName(['chasisCalculation', 'type'])}
													label="Jenis Chasis"
													rules={[
														{
															required: true,
															message: 'Jenis Chasis wajib diisi',
														},
													]}
												>
													<Input style={{ width: '100%' }} />
												</Form.Item>
											</Col>
											<Col span={12}>
												<Form.Item
													name={fieldName([
														'chasisCalculation',
														'bpkbOwnerName',
													])}
													label="Atas Nama BPKB"
												>
													<Input style={{ width: '100%' }} />
												</Form.Item>
											</Col>
										</Row>

										<Divider />

										<Row gutter={40}>
											<Col span={12}>
												<Form.Item
													name={fieldName(['chasisCalculation', 'unitPrice'])}
													label="Harga Barang/Unit"
													rules={[
														{
															required: true,
															message: 'Harga Barang wajib diisi',
														},
													]}
												>
													<InputNumber
														style={{ width: '100%' }}
														formatter={(value) =>
															value
																? value
																		.toString()
																		.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
																: ''
														}
														parser={(value) =>
															value ? value.replace(/\D/g, '') : ''
														}
													/>
												</Form.Item>
											</Col>
											<Col span={12}>
												<Form.Item
													// name={fieldName(['chasisCalculation', 'totalPrice'])} // No need to send total
													label="Total Harga"
												>
													<InputNumber
														style={{ width: '100%' }}
														formatter={(value) =>
															value
																? value
																		.toString()
																		.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
																: ''
														}
														readOnly
														disabled
														value={chasisCalculationUnitPrice * totalUnit}
													/>
												</Form.Item>
											</Col>
										</Row>

										<Divider />

										<Row gutter={40}>
											<Col span={12}>
												<Row gutter={12}>
													<Col span={12}>
														<Form.Item
															name={fieldName([
																'chasisCalculation',
																'downPaymentPercentage',
															])}
															label="Down Payment"
															rules={[
																{
																	required: true,
																	message: 'Harga Barang wajib diisi',
																},
																{
																	validator(_, value) {
																		if (
																			value <
																			simulationDetailData.minimumDownPaymentPercentage
																		) {
																			return Promise.reject(
																				'Net Down Payment yang Anda inputkan berada di bawah ketentuan yang berlaku. Silakan perbarui Nett Down Payment sesuai ketentuan.',
																			);
																		}
																		return Promise.resolve();
																	},
																},
															]}
														>
															<InputNumber
																style={{ width: '100%' }}
																min={0}
																max={100}
																addonAfter="%"
															/>
														</Form.Item>
													</Col>
													<Col span={12}>
														<InputNumber
															value={chasisDownPayment}
															formatter={(value) =>
																value
																	? value
																			.toString()
																			.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
																	: ''
															}
															disabled
															readOnly
															style={{ width: '100%' }}
														/>
													</Col>
												</Row>
											</Col>
										</Row>

										<Divider />
									</>
								)}

								{/* End Of Split Mode */}
								<span style={{ fontSize: 24, fontWeight: 'bold' }}>
									Total Perhitungan
								</span>

								<Row gutter={40}>
									{assetCondition === SIMULATION_ASSET_CONDITION.USED &&
									(assetType === SIMULATION_ASSET_TYPE.PASSENGER ||
										assetType === SIMULATION_ASSET_TYPE.COMMERCIAL) ? (
										<Col span={12}>
											<Form.Item
												name={fieldName('phJmbAmount')}
												label="Harga PHJMB"
											>
												<InputNumber
													style={{ width: '100%' }}
													formatter={(value) =>
														value
															? value
																	.toString()
																	.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
															: ''
													}
													parser={(value) =>
														value ? value.replace(/\D/g, '') : ''
													}
													disabled
												/>
											</Form.Item>
										</Col>
									) : null}
								</Row>
								<Divider />
								<Row gutter={40}>
									<Col span={12}>
										<Form.Item
											name={fieldName('requestedOtrAmount')}
											label="OTR Pengajuan"
											rules={[
												{
													required: true,
													message: 'OTR Pengajuan wajib diisi',
												},
												{
													validator(_, value) {
														const assetConditionInForm = form.getFieldValue(
															fieldName('assetConditionId'),
														);
														const assetTypeInForm = form.getFieldValue(
															fieldName('assetType'),
														);
														const phJmbAmount = form.getFieldValue(
															fieldName('phJmbAmount'),
														);

														// Check if asset type is passenger or commercial car AND condition is used
														const isPassengerOrCommercial =
															assetTypeInForm ===
																SIMULATION_ASSET_TYPE.PASSENGER ||
															assetTypeInForm ===
																SIMULATION_ASSET_TYPE.COMMERCIAL;

														const isUsedCondition =
															assetConditionInForm ===
															SIMULATION_ASSET_CONDITION.USED;

														// If conditions met and PHJMB > 0, validate OTR Pengajuan <= PHJMB
														if (
															isPassengerOrCommercial &&
															isUsedCondition &&
															phJmbAmount > 0
														) {
															if (value > phJmbAmount) {
																return Promise.reject(
																	`OTR Pengajuan tidak boleh melebihi Harga PHJMB (${new Intl.NumberFormat('id-ID').format(phJmbAmount)})`,
																);
															}
														}

														return Promise.resolve();
													},
												},
											]}
										>
											<InputNumber
												style={{ width: '100%' }}
												formatter={(value) =>
													value
														? value
																.toString()
																.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
														: ''
												}
												parser={(value) =>
													value ? value.replace(/\D/g, '') : ''
												}
												disabled={isHeavyEquipmentAndOtherComponent}
											/>
										</Form.Item>
										{assetType === SIMULATION_ASSET_TYPE.HEAVY_EQUIPMENT ? (
											<Form.Item
												name={fieldName('isHeavyEquipmentAndOtherComponent')}
												rules={[{ required: false }]}
												// For checkboxes, labelCol/wrapperCol might need adjustment, or use noStyle with custom layout
											>
												<Checkbox
													onChange={(e) => {
														form.setFieldValue(
															fieldName('isHeavyEquipmentAndOtherComponent'),
															e.target.checked,
														);
													}}
													checked={isHeavyEquipmentAndOtherComponent}
													value={isHeavyEquipmentAndOtherComponent}
												/>
												<span style={{ marginLeft: 10, fontSize: 12 }}>
													Checklist apabila unit yang diajukan merupakan Heavy
													Equipment + Komponen Lainnya
												</span>
											</Form.Item>
										) : null}
										{assetType === SIMULATION_ASSET_TYPE.COMMERCIAL &&
										!isSplitPo ? (
											<Form.Item
												name={fieldName('isChasisAndKaroseri')}
												rules={[{ required: false }]}
												// For checkboxes, labelCol/wrapperCol might need adjustment, or use noStyle with custom layout
											>
												<Checkbox
													onChange={(e) => {
														form.setFieldValue(
															fieldName('isChasisAndKaroseri'),
															e.target.checked,
														);
													}}
													checked={isChasisAndKaroseri}
													value={isChasisAndKaroseri}
												/>
												<span style={{ marginLeft: 10, fontSize: 12 }}>
													Checklist apabila unit yang diajukan merupakan Chasis
													+ Karoseri
												</span>
											</Form.Item>
										) : null}
									</Col>
									<Col span={12}>
										<Form.Item
											name={fieldName('otrSubsidyAmount')}
											label="Subsidi OTR"
										>
											<InputNumber
												style={{ width: '100%' }}
												formatter={(value) =>
													value
														? value
																.toString()
																.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
														: ''
												}
												parser={(value) =>
													value ? value.replace(/\D/g, '') : ''
												}
											/>
										</Form.Item>
									</Col>
								</Row>

								{isHeavyEquipmentAndOtherComponent && (
									<>
										<Divider />

										<Row gutter={40}>
											<Col span={12}>
												<Form.Item
													name={fieldName('otrHeavyEquipmentAmount')}
													label="OTR Heavy Equipment"
													rules={[
														{
															required: true,
															message: 'OTR Heavy Equipment wajib diisi',
														},
													]}
												>
													<InputNumber
														style={{ width: '100%' }}
														formatter={(value) =>
															value
																? value
																		.toString()
																		.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
																: ''
														}
														onChange={onChangeOtrHeHe}
													/>
												</Form.Item>
											</Col>
											<Col span={12}>
												<Form.Item
													name={fieldName(
														'otrOtherHeavyEquipmentComponentAmount',
													)}
													label="OTR Komponen Lainnya"
													rules={[
														{
															required: true,
															message: 'OTR Komponen Lainnya wajib diisi',
														},
													]}
												>
													<InputNumber
														style={{ width: '100%' }}
														formatter={(value) =>
															value
																? value
																		.toString()
																		.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
																: ''
														}
														onChange={onChangeOtrHeOth}
													/>
												</Form.Item>
											</Col>
										</Row>
									</>
								)}

								{isChasisAndKaroseri && (
									<>
										<Divider />

										<Row gutter={40}>
											<Col span={12}>
												<Form.Item
													name={fieldName('otrChasisAmount')}
													label="OTR Chasis"
													rules={[
														{
															required: true,
															message: 'OTR Chasis wajib diisi',
														},
													]}
												>
													<InputNumber
														style={{ width: '100%' }}
														formatter={(value) =>
															value
																? value
																		.toString()
																		.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
																: ''
														}
														onChange={onChangeOtrChasKarChas}
													/>
												</Form.Item>
											</Col>
											<Col span={12}>
												<Form.Item
													name={fieldName('otrKaroseriAmount')}
													label="OTR Karoseri"
													rules={[
														{
															required: true,
															message: 'OTR Karoseri wajib diisi',
														},
													]}
												>
													<InputNumber
														style={{ width: '100%' }}
														formatter={(value) =>
															value
																? value
																		.toString()
																		.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
																: ''
														}
														onChange={onChangeOtrChasKarKar}
													/>
												</Form.Item>
											</Col>
										</Row>
									</>
								)}

								{(assetType === SIMULATION_ASSET_TYPE.HEAVY_EQUIPMENT ||
									assetType === SIMULATION_ASSET_TYPE.MACHINE) &&
								assetCondition === SIMULATION_ASSET_CONDITION.USED ? (
									<>
										<Divider />
										<Row gutter={40}>
											<Col span={12} />
											<Col span={12}>
												<Form.Item
													name={fieldName('kjppLiquidityAmount')}
													label="KJPP Likuiditas"
													rules={[
														{
															required: false,
														},
													]}
												>
													<InputNumber
														style={{ width: '100%' }}
														formatter={(value) =>
															value
																? value
																		.toString()
																		.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
																: ''
														}
													/>
												</Form.Item>
											</Col>
										</Row>
									</>
								) : null}
								<Divider />

								<Row gutter={40}>
									<Col span={12}>
										<Form.Item
											name={fieldName('downPaymentPercentage')}
											label="Down Payment"
											rules={[
												{ required: true, message: 'Down Payment wajib diisi' },
												{
													validator(_, value) {
														if (
															value <
															simulationDetailData.minimumDownPaymentPercentage
														) {
															return Promise.reject(
																'Net Down Payment yang Anda inputkan berada di bawah ketentuan yang berlaku. Silakan perbarui Nett Down Payment sesuai ketentuan.',
															);
														}
														return Promise.resolve();
													},
												},
											]}
										>
											<InputNumber
												min={0}
												max={100}
												addonAfter="%"
												style={{ width: '100%' }}
											/>
										</Form.Item>
									</Col>
									<Col span={12}>
										<Form.Item
											name={fieldName('effectiveRatePercentage')}
											label="Eff Rate"
											rules={[
												{ required: true, message: 'Eff Rate wajib diisi' },
												{
													validator(_, value) {
														if (
															value <
															simulationDetailData.minimumEffectiveRatePercentage
														) {
															return Promise.reject(
																'Eff Rate yang Anda inputkan berada di bawah ketentuan yang berlaku. Silakan perbarui Eff Rate sesuai ketentuan.',
															);
														}
														return Promise.resolve();
													},
												},
											]}
										>
											<InputNumber
												min={0}
												max={100}
												addonAfter="%"
												style={{ width: '100%' }}
											/>
										</Form.Item>
									</Col>
								</Row>
								<Divider />

								<Row gutter={40}>
									<Col span={12}>
										<Form.Item
											name={fieldName('tenorInYear')}
											label="Tenor Tahun"
											rules={[
												{ required: true, message: 'Tenor Tahun wajib diisi' },
											]}
										>
											<Select
												options={mapOptionsToSelect(validTenorInYearOptions)}
												style={{ width: '100%' }}
											/>
										</Form.Item>
									</Col>
									<Col span={12}>
										<Form.Item
											name={fieldName('totalUnit')}
											label="Jumlah Unit"
											rules={[
												{ required: true, message: 'Jumlah Unit wajib diisi' },
											]}
										>
											<InputNumber
												min={1}
												style={{ width: '100%' }}
											/>
										</Form.Item>
									</Col>
								</Row>
								<Divider />

								{calcType !== SIMULATION_CALCULATION_TYPE_OPTIONS.FIXED &&
									tenorInYear && (
										<>
											<span>Komposisi Angsuran Per Tahun</span>
											<Row gutter={40}>
												{[...Array(Number(tenorInYear) || 0)].map((_, idx) => {
													const year = idx + 1;
													return (
														<Col
															span={12}
															key={year}
														>
															<Form.Item
																name={fieldName([
																	'paymentBreakdownPercentages',
																	year - 1,
																])}
																label={`Tahun ${year}`}
																rules={[
																	{
																		required: true,
																		message: `Komposisi Angsuran Tahun ${year} wajib diisi`,
																	},
																	validatePaymentBreakdown(
																		form,
																		tenorInYear,
																		calcType,
																	),
																]}
															>
																<InputNumber
																	min={1}
																	max={100}
																	addonAfter="%"
																	style={{ width: '100%' }}
																/>
															</Form.Item>
														</Col>
													);
												})}
											</Row>
											<Divider />
										</>
									)}

								<Row gutter={40}>
									<Col span={12}>
										<Form.Item
											name={fieldName('provisionPayment')}
											label="Pembayaran Provisi"
										>
											<Input
												readOnly
												disabled
												style={{ width: '100%' }}
											/>
										</Form.Item>
									</Col>
									<Col span={12}>
										<Form.Item
											name={fieldName('provisionPercentage')}
											label="Provisi"
											rules={[
												{ required: true, message: 'Provisi wajib diisi' },
												{
													validator(_, value) {
														if (
															Number(value) <
															Math.min(
																simulationDetailData.minimumProvisionPercentage,
																100,
															)
														) {
															return Promise.reject(
																'Provisi yang Anda inputkan berada di bawah ketentuan yang berlaku. Silakan perbarui Provisi sesuai ketentuan.',
															);
														}
														return Promise.resolve();
													},
												},
											]}
										>
											<InputNumber
												min={0}
												max={100}
												addonAfter="%"
												style={{ width: '100%' }}
											/>
										</Form.Item>
									</Col>
								</Row>
								<Divider />

								<Row gutter={40}>
									<Col span={12}>
										<Form.Item
											name={fieldName('servicingFee')}
											label="Servicing Fee"
										>
											<InputNumber
												style={{ width: '100%' }}
												formatter={(value) =>
													value
														? value
																.toString()
																.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
														: ''
												}
												parser={(value) =>
													value ? value.replace(/\D/g, '') : ''
												}
												disabled
											/>
										</Form.Item>
									</Col>
								</Row>
								<Divider />

								<Row gutter={40}>
									<Col span={12}>
										<Form.Item
											name={fieldName('administrationPayment')}
											label="Pembayaran Admin"
										>
											<Input
												readOnly
												disabled
												style={{ width: '100%' }}
											/>
										</Form.Item>
									</Col>
									<Col span={12}>
										<Form.Item
											name={fieldName('administrationFee')}
											label="Admin"
											rules={[
												{ required: true, message: 'Admin wajib diisi' },

												{
													validator(_, value) {
														if (
															value <
															simulationDetailData.minimumAdministrationFee
														) {
															// TODO: Get limit from call data detail fasilitas table
															return Promise.reject(
																'Biaya Admin yang Anda inputkan berada di bawah ketentuan yang berlaku. Silakan perbarui Biaya Admin sesuai ketentuan.',
															);
														}
														return Promise.resolve();
													},
												},
											]}
										>
											<InputNumber
												style={{ width: '100%' }}
												formatter={(value) =>
													value
														? value
																.toString()
																.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
														: ''
												}
												parser={(value) =>
													value ? value.replace(/\D/g, '') : ''
												}
											/>
										</Form.Item>
										<Alert
											message="Biaya admin yang diinputkan untuk single unit."
											type="warning"
											showIcon
											className="custom-alert-info"
										/>
									</Col>
								</Row>
								<Divider />

								<Row gutter={40}>
									<Col span={12}>
										<Form.Item
											name={fieldName('fiduciaryPayment')}
											label="Pembayaran Fidusia"
										>
											<Input
												readOnly
												disabled
												style={{ width: '100%' }}
											/>
										</Form.Item>
									</Col>
									<Col span={12}>
										<Form.Item
											name={fieldName('fiduciaryFee')}
											label="Fidusia"
											rules={[
												{ required: true, message: 'Fidusia wajib diisi' },
											]}
										>
											<InputNumber
												style={{ width: '100%' }}
												formatter={(value) =>
													value
														? value
																.toString()
																.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
														: ''
												}
												parser={(value) =>
													value ? value.replace(/\D/g, '') : ''
												}
												disabled
											/>
										</Form.Item>
										<Form.Item
											name={fieldName('isBundlingFiduciaryFee')}
											rules={[{ required: false }]}
											// For checkboxes, labelCol/wrapperCol might need adjustment, or use noStyle with custom layout
										>
											<Checkbox
												onChange={(e) =>
													form.setFieldValue(
														fieldName('isBundlingFiduciaryFee'),
														e.target.checked,
													)
												}
												checked={isBundlingFiduciaryFee}
												value={isBundlingFiduciaryFee}
											/>
											<span style={{ marginLeft: 10, fontSize: 12 }}>
												Checklist apabila bundling
											</span>
										</Form.Item>
										{isBundlingFiduciaryFee && (
											<InputNumber
												style={{ width: '100%' }}
												addonAfter="/unit"
												formatter={(value) =>
													value
														? value
																.toString()
																.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
														: ''
												}
												disabled
												value={fiduciaryFee / totalUnit}
											/>
										)}
										<Alert
											message="Biaya fidusia yang diinputkan untuk bundling atau single unit"
											type="warning"
											showIcon
											className="custom-alert-info"
										/>
									</Col>
								</Row>
								<Divider />

								<Row gutter={40}>
									<Col span={12}>
										<Form.Item
											name={fieldName('totalDownPaymentSubsidyAmount')}
											label="Subsidi TDP"
										>
											<InputNumber
												style={{ width: '100%' }}
												formatter={(value) =>
													value
														? value
																.toString()
																.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
														: ''
												}
												parser={(value) =>
													value ? value.replace(/\D/g, '') : ''
												}
											/>
										</Form.Item>
									</Col>
								</Row>
							</div>
						</Collapse.Panel>
						<Collapse.Panel
							header="Simulasi Asuransi"
							key="insurance-section"
						>
							<SimInsurance
								form={form}
								facilityId={facilityId!}
								fieldName={fieldName}
								simulationDetailData={simulationDetailData!}
								uploadMutation={uploadMutation}
								viewOnly={viewOnly}
								tenorInYear={Number(tenorInYear) || 1}
							/>
						</Collapse.Panel>
					</Collapse>
					<div
						style={{
							display: 'flex',
							justifyContent: 'flex-end',
							marginTop: 32,
						}}
					>
						<Button
							type="primary"
							htmlType={showResult ? 'button' : 'submit'}
							size="large"
							disabled={
								(viewOnly ||
									!(
										userData?.groupName === 'FCOH' ||
										userData?.groupName === 'FRM'
									)) &&
								!showResult
							}
							onClick={
								showResult
									? () => {
											setShowCalculation(true);
											setShouldShowResult(true);
										}
									: undefined
							}
						>
							{showResult ? 'Show Calculation' : 'Submit'}
						</Button>
					</div>
				</Form>
			</div>
			{/* Confirmation Modal */}
			<FleetModal
				open={confirmOpen}
				onCancel={handleCancelSubmit}
				closable={false}
				maskClosable={false}
				footer={null}
				width={420}
				style={{
					padding: '36px 32px 24px 32px',
					borderRadius: 12,
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					width: '100%',
					textAlign: 'center',
				}}
			>
				<div style={{ display: 'flex', justifyContent: 'center' }}>
					<InfoCircleFilled
						style={{ fontSize: 48, color: '#1557BC', marginBottom: 16 }}
					/>
				</div>
				<div style={{ fontWeight: 600, fontSize: 18, marginBottom: 32 }}>
					Apakah simulasi kredit yang Anda inputkan telah sesuai?
				</div>
				<div
					style={{
						display: 'flex',
						justifyContent: 'center',
						gap: 16,
						width: '100%',
						marginBottom: 24,
					}}
				>
					<Button
						onClick={handleCancelSubmit}
						type="default"
						style={{
							minWidth: 120,
							fontWeight: 600,
							fontSize: 16,
							borderRadius: 6,
							border: '1px solid #1557BC',
							color: '#1557BC',
							background: 'white',
						}}
					>
						TIDAK
					</Button>
					<Button
						onClick={handleConfirmSubmit}
						type="primary"
						style={{
							minWidth: 120,
							fontWeight: 600,
							fontSize: 16,
							borderRadius: 6,
							background: '#1557BC',
							color: 'white',
							border: 'none',
						}}
					>
						YA
					</Button>
				</div>
				<div
					style={{
						position: 'absolute',
						left: 0,
						bottom: 0,
						width: '100%',
						height: 8,
						display: 'flex',
					}}
				>
					<div
						style={{ background: '#1557BC', height: '100%', width: '85%' }}
					/>
					<div
						style={{ background: '#FFB300', height: '100%', width: '15%' }}
					/>
				</div>
			</FleetModal>
		</>
	);
};

export default FRMSimulationPage;
